import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Layout } from "./components/Layout";
import { AuthGuard } from "./components/AuthGuard";
import { RequireAdmin } from "./components/RequireAdmin";
import { AuthProvider } from "./lib/auth";
import { RoleProvider } from "./lib/role";
import { DashboardPage } from "./pages/Dashboard";
import { RunsPage } from "./pages/Runs";
import { RunDetailPage } from "./pages/RunDetail";
import { InputsPage } from "./pages/Inputs";
import { SettingsPage } from "./pages/Settings";
import { TestUploadPage } from "./pages/TestUpload";
import { UploadsPage } from "./pages/Uploads";
import { UploadDetailPage } from "./pages/UploadDetail";
import { SisBundlesPage } from "./pages/SisBundles";
import { SisBundleDetailPage } from "./pages/SisBundleDetail";
import { AuthCallbackPage } from "./pages/AuthCallback";

/** Param-preserving redirect for old /runs/:runId bookmarks. */
function LegacyRunRedirect() {
  const { runId } = useParams<{ runId: string }>();
  return <Navigate to={`/syncs/${runId}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* /auth/callback must be reachable without an existing session —
            it's the page Okta redirects to with the OIDC code. */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="*"
          element={
            <AuthGuard>
              {/* RoleProvider blocks rendering until GET /api/me resolves;
                  users with no recognized Okta group get a full-screen
                  access-denied instead of the app shell. */}
              <RoleProvider>
                <Layout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/syncs" element={<RunsPage />} />
                    <Route
                      path="/syncs/new"
                      element={
                        <RequireAdmin>
                          <InputsPage />
                        </RequireAdmin>
                      }
                    />
                    <Route path="/syncs/:runId" element={<RunDetailPage />} />
                    <Route path="/uploads" element={<UploadsPage />} />
                    <Route
                      path="/uploads/:uploadId"
                      element={<UploadDetailPage />}
                    />
                    <Route path="/sis-bundles" element={<SisBundlesPage />} />
                    <Route
                      path="/sis-bundles/:bundleId"
                      element={<SisBundleDetailPage />}
                    />
                    {/* Legacy paths — the run→sync rename and earlier page
                        moves; old bookmarks land on the new routes. */}
                    <Route
                      path="/runs"
                      element={<Navigate to="/syncs" replace />}
                    />
                    <Route
                      path="/runs/:runId"
                      element={<LegacyRunRedirect />}
                    />
                    <Route
                      path="/inputs"
                      element={<Navigate to="/syncs/new" replace />}
                    />
                    <Route
                      path="/outputs"
                      element={<Navigate to="/syncs" replace />}
                    />
                    <Route
                      path="/test-upload"
                      element={
                        <RequireAdmin>
                          <TestUploadPage />
                        </RequireAdmin>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <RequireAdmin>
                          <SettingsPage />
                        </RequireAdmin>
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </RoleProvider>
            </AuthGuard>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
